// @flow
import BaseModel from 'Stores/BaseModel';
import ListModel from 'Stores/ListModel';
import AssetModel from 'Stores/AssetModel';
import {
  formatDate,
  fromApiDate,
  formatDateToISO,
  dateIsBefore,
  dateIsAfter,
  dateStartOfDay,
  dateEndOfDay,
  addMonthsToDate,
  sanitizeTags,
  sanitizeUrl,
} from 'Utils/index';
import {
  validateLength,
  isInvalid,
  validateImageSelect,
  validateCTAUrl,
  validateRTELength,
  validateNoLocalFootnotes,
} from 'Utils/Validators';
import axios from 'axios';
import { withInheritQueryParam } from 'Utils/withInheritQueryParam';

declare var API_BASE_URL: string;

export default class DealerToolboxModel extends BaseModel {
  static INDEX_FREE_URL = 4; // simply the index of Freier Link since it's a special thing

  // the ctas a dealer can set in a structure Component/Dropdown can handle em
  static ctas = [
    {
      href: null,
      label: 'Kein Button',
      mazdaForm: false,
      target: null,
      type: null,
      value: 1,
    },
    {
      href: '/probefahrt',
      label: 'Probefahrt anfragen',
      mazdaForm: true,
      target: null,
      type: 'TEST_DRIVE',
      value: 2,
    },
    {
      href: '/angebotsanfrage',
      label: 'Angebotsanfrage',
      mazdaForm: true,
      target: null,
      type: 'REQUEST_FOR_PROPOSAL',
      value: 3,
    },
    {
      href: '/?modaltype=REQUEST_FOR_CONSULTATION&showmodal=true',
      label: 'Termin vereinbaren',
      mazdaForm: false,
      target: null,
      type: null,
      value: 4,
    },
    {
      href: null,
      label: 'Freier Link',
      mazdaForm: false,
      target: null,
      type: null,
      value: 5,
    },
  ];

  // the cta-items a dealer can set
  static __ctaItems = [
    'introCta1',
    'introCta2',
    'offerCta1',
    'offerCta2',
    'closingCta1',
    'closingCta2',
  ];

  // https://confluence.customers.sevenval.com/pages/viewpage.action?pageId=54791521#H%C3%A4ndlerbaukasten-L%C3%B6sungsidee
  // also: docs/api/openapi.yaml
  // * = required
  id: number = 0;
  dealerId: string = '';
  publishingDateActivated: boolean = false;
  publishingDateStart: ?string = new Date();
  publishingDateEnd: ?string = null;
  datePublished: ?Date = new Date();
  dateUpdated: ?Date = new Date();
  hidden: boolean = false;
  heroTitle: string = ''; // *
  heroImage: ?AssetModel = null; // *
  introTitle: ?string = null;
  introText: ?string = null;
  introCta1Title: ?string = null;
  introCta1Url: ?string = null;
  introCta2Title: ?string = null;
  introCta2Url: ?string = null;
  offerTitle: ?string = null;
  offerImage: ?AssetModel = null; // *
  offerText: string = ''; // *
  offerCta1Title: ?string = null;
  offerCta1Url: ?string = null;
  offerCta2Title: ?string = null;
  offerCta2Url: ?string = null;
  closingTitle: ?string = null;
  closingText: ?string = null;
  closingCta1Title: ?string = null;
  closingCta1Url: ?string = null;
  closingCta2Title: ?string = null;
  closingCta2Url: ?string = null;
  consumptionInfo: ?string = null;
  consumptionText: ?string = null;
  consumptionInfoCustomDescriptionEnabled: ?boolean = false;
  consumptionInfoCustomDescription: ?string = null;

  managePublishing: string = 'online';

  // [__ctaItem] = ctas[x].value will be injected here by the contructor

  constructor(data: Object = {}) {
    super();
    this.hydrate(data);

    this.initImage('heroImage', data);
    this.initImage('offerImage', data);
    this.datePublished = fromApiDate(data.datePublished);
    this.dateUpdated = fromApiDate(data.dateUpdated);
    this.publishingDateStart = fromApiDate(data.publishingDateStart);
    this.publishingDateEnd = data.publishingDateEnd ? fromApiDate(data.publishingDateEnd) : null;
    this.consumptionInfoCustomDescriptionEnabled = !!data.consumptionInfoCustomDescriptionEnabled;

    this.managePublishing =
      this.hidden && !this.isNew()
        ? 'offline'
        : this.publishingDateActivated
          ? 'timeset'
          : 'online';

    this.everyCtaItem((label) => {
      // no url => no item
      if (this[`${label}Url`] === null || this[`${label}Url`].length === 0) {
        this[label] = DealerToolboxModel.ctas[0].value;
        return;
      }

      const cta = DealerToolboxModel.ctas.find((cta) => this[`${label}Title`] === cta.label);

      if (cta === undefined) {
        this[label] = DealerToolboxModel.ctas[DealerToolboxModel.INDEX_FREE_URL].value;
      } else {
        this[label] = cta.value;
      }
    });
  }

  initImage = (key: string, data: {}) => {
    if (typeof this[key] === 'number') {
      this[key] = new AssetModel({ id: this.heroImage });
    } else {
      this[key] = data[key] ? new AssetModel(data[key]) : null;
    }
  };

  everyCtaItem = (callback) => DealerToolboxModel.__ctaItems.forEach(callback);

  hasCTAError = (label: string) => !!this.errors[`${label}Title`] || !!this.errors[`${label}Url`];

  static isFreeText = (cta: number) =>
    cta === DealerToolboxModel.ctas[DealerToolboxModel.INDEX_FREE_URL].value;

  hasCtaSet = (key: string) =>
    this[`${key}Cta1`] !== DealerToolboxModel.ctas[0].value ||
    this[`${key}Cta2`] !== DealerToolboxModel.ctas[0].value;

  hasModule = (mod: string) => {
    switch (mod) {
      case 'intro':
        return !!this.introTitle || !!this.introText || this.hasCtaSet('intro');
      default:
        // === closing
        return !!this.closingTitle || !!this.closingText || this.hasCtaSet('closing');
    }
  };

  // create a map of all used ctas
  getAllCtas = () => {
    const re = {};

    this.everyCtaItem((item) => {
      const cta = this.getCta(item);

      if (!!cta) {
        re[item] = cta;
      }
    });

    return re;
  };

  // transform a cta to a usable cta-object by its label
  getCta = (label: string) => {
    if (this[label] === DealerToolboxModel.ctas[0].value) {
      return null;
    }

    if (DealerToolboxModel.isFreeText(this[label])) {
      return {
        label: this[`${label}Title`],
        href: this[`${label}Url`],
      };
    }

    return DealerToolboxModel.ctas.find((item) => item.value === this[label]);
  };

  populatePublishingEndDate = (): ?Date => {
    // populate only for new event records
    if (this.isNew() && !this.publishingDateEnd) {
      this.publishingDateEnd = addMonthsToDate(this.startDate, 3);

      return this.publishingDateEnd;
    }

    return null;
  };

  valid = (opts = { noLocalFootnotes: false }) => {
    this.errors = {};
    validateLength(this.errors, 'heroTitle', this.heroTitle, 1, 45);
    validateLength(this.errors, 'introTitle', this.introTitle || '', 0, 50);
    validateRTELength(this.errors, 'introText', sanitizeTags(this.introText || ''), 0, 1000);
    validateLength(this.errors, 'offerTitle', this.offerTitle || '', 0, 50);
    validateRTELength(this.errors, 'offerText', sanitizeTags(this.offerText || ''), 1, 1000);
    validateLength(this.errors, 'closingTitle', this.closingTitle || '', 0, 50);
    validateRTELength(this.errors, 'closingText', sanitizeTags(this.closingText || ''), 0, 1000);
    validateImageSelect(this.errors, 'heroImage', this.heroImage);
    validateImageSelect(this.errors, 'offerImage', this.offerImage);

    if (opts.noLocalFootnotes) {
      if (!this.errors.introText) {
        validateNoLocalFootnotes(this.errors, 'introText', this.introText);
      }

      if (!this.errors.offerText) {
        validateNoLocalFootnotes(this.errors, 'offerText', this.offerText);
      }

      if (!this.errors.closingText) {
        validateNoLocalFootnotes(this.errors, 'closingText', this.closingText);
      }
    }

    this.everyCtaItem((label) => {
      // if no button / a fixed thing it does not need a validation
      if (this[label] !== DealerToolboxModel.ctas[DealerToolboxModel.INDEX_FREE_URL].value) {
        delete this.errors[`${label}Title`];
        delete this.errors[`${label}Url`];

        return;
      }

      validateLength(this.errors, `${label}Title`, this[`${label}Title`] || '', 1, 25);
      validateCTAUrl(this.errors, `${label}Url`, this[`${label}Url`] || '', false);
    });

    return !isInvalid(this.errors);
  };

  save = (inherit?: boolean) => {
    const data = this.toRequestJSON();

    if (this.id > 0) {
      return axios
        .patch(withInheritQueryParam(`${API_BASE_URL}/dealer-toolbox/${this.id}/`, inherit), data)
        .then((res) => new DealerToolboxModel(res.data));
    }

    return axios
      .post(withInheritQueryParam(`${API_BASE_URL}/dealer-toolbox/`, inherit), data)
      .then((res) => new DealerToolboxModel(res.data));
  };

  delete = () => axios.delete(`${API_BASE_URL}/dealer-toolbox/${this.id}/`);

  toListItem() {
    const subline = [`Aktion | Erstellt: ${formatDate(this.datePublished, 'dd.MM.yyyy')}`];

    if (this.publishingDateActivated && this.publishingDateStart && this.publishingDateEnd) {
      subline.push('|');
      subline.push(
        `Sichtbarkeit: ${formatDate(this.publishingDateStart, 'dd.MM.yyyy')} - ${formatDate(
          this.publishingDateEnd,
          'dd.MM.yyyy'
        )}`
      );
    } else if (this.publishingDateActivated && this.publishingDateStart) {
      subline.push('|');
      subline.push(`Sichtbarkeit: ${formatDate(this.publishingDateStart, 'dd.MM.yyyy')} - offen`);
    } else if (this.publishingDateActivated) {
      subline.push('|');
      subline.push('Sichtbarkeit: Manuell');
    }

    const now = new Date();
    const startOfPublishingStartDate = dateStartOfDay(this.publishingDateStart);
    const endOfPublishingEndDate = dateEndOfDay(this.publishingDateEnd);
    const isHidden =
      this.hidden ||
      (this.publishingDateActivated && dateIsBefore(now, startOfPublishingStartDate)) ||
      (this.publishingDateActivated && dateIsAfter(now, endOfPublishingEndDate));

    return {
      id: this.id,
      headline: this.heroTitle,
      subline: subline.join(' '),
      hidden: isHidden,
      image: this.heroImage,
      model: this,
      publishingDateActivated: this.publishingDateActivated,
      publishingDateStart: this.publishingDateStart,
      publishingDateEnd: this.publishingDateEnd,
    };
  }

  toJSON = () => {
    const json: Object = {
      dealerId: this.dealerId,
      publishingDateActivated: this.publishingDateActivated,
      publishingDateStart: this.publishingDateStart,
      publishingDateEnd: this.publishingDateEnd,
      hidden: this.hidden,
      heroTitle: this.heroTitle,
      heroImage: this.heroImage,
      introTitle: this.introTitle,
      introText: this.introText,
      offerTitle: this.offerTitle,
      offerImage: this.offerImage,
      offerText: this.offerText,
      closingTitle: this.closingTitle,
      closingText: this.closingText,
      consumptionInfo: this.consumptionInfo,
      consumptionInfoCustomDescriptionEnabled: !!this.consumptionInfoCustomDescriptionEnabled,
      consumptionInfoCustomDescription: this.consumptionInfoCustomDescription,
      introCta1Title: null,
      introCta2Title: null,
      offerCta1Title: null,
      offerCta2Title: null,
      closingCta1Title: null,
      closingCta2Title: null,
      introCta1Url: null,
      introCta2Url: null,
      offerCta1Url: null,
      offerCta2Url: null,
      closingCta1Url: null,
      closingCta2Url: null,
    };

    const ctas = this.getAllCtas();

    this.everyCtaItem((label) => {
      if (ctas[label]) {
        json[`${label}Title`] = ctas[label].label;
        json[`${label}Url`] = ctas[label].href;
      }
    });

    if (!this.isNew()) {
      json.id = this.id;
    }

    return json;
  };

  /**
   * Process request data to ensure proper casting and formatting.
   *
   * @return {Object}
   */
  toRequestJSON: Object = () => {
    const res = this.toJSON();

    // cast date field to ISO 8601 string securely due to formatting errors with
    // timezone when axios uses JSON.stringify() to cast date objects to string
    ['publishingDateStart', 'publishingDateEnd'].forEach((field) => {
      if (res.hasOwnProperty(field)) {
        res[field] = !!res[field] ? formatDateToISO(res[field]) : null;
      }
    });

    this.everyCtaItem((label) => {
      res[`${label}Url`] = sanitizeUrl(res[`${label}Url`]);
    });

    return res;
  };

  static getAll = (params: Object = {}) =>
    axios({
      method: 'get',
      url: `${API_BASE_URL}/dealer-toolbox/`,
      params,
    }).then((res: { data: Array<Object> }) => new ListModel(res.data, DealerToolboxModel));
}
